import React, { useState } from 'react';
import {
    NativeSyntheticEvent,
    TextInputKeyPressEventData
} from 'react-native';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import ProvidenceModal from './ProvidenceModal';
import DeleteModal from '../../../components/DeleteModal';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { RootNavigation } from '../../../Routes';

export default function Providences({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'Providences'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['Providences']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState(false);
    const [selectedProvidence, setSelectedProvidence] = useState<Providence>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedProvidences', filters],
        queryFn: () => dispatchService.getPaginatedProvidences(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleEditClick(providence: Providence) {
        setSelectedProvidence(providence);
        setModalVisible(true);
    }

    function showDeleteConfirmationDialog(providence: Providence) {
        setSelectedProvidence(providence);
        setDeleteModalVisible(true);
    }

    async function confirmDelete(providence: Providence) {
        try {
            await dispatchService.deleteProvidence(providence.id);
            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    function handleFormClose() {
        setSelectedProvidence(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('providences')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setSelectedProvidence(undefined);
                            setModalVisible(true);
                        }}
                    />
                }
            />
            <Card>
                <DataTableHeader
                    zIndex={2}
                    title={translate('registeredProvidences')}
                    filter={
                        <Filter
                            filters={filters}
                            setFilters={setFilters}
                            textFilter={textFilter}
                            setTextFilter={setTextFilter}
                        />
                    }
                    filterInput={
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                            onKeyPress={handleKeyDown}
                        />
                    }
                />

                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            columns={[
                                {
                                    name: 'ID',
                                    selector: row => row.id,
                                    grow: 0
                                },
                                {
                                    name: translate('name'),
                                    selector: row => row.name,
                                    wrap: true,
                                    grow: 3
                                },
                                {
                                    name: translate('actingBody'),
                                    selector: row => row.actingBody.name,
                                    wrap: true,
                                },
                                {
                                    button: true,
                                    cell: row => (
                                        <DropdownButton
                                            actions={[
                                                {
                                                    content: translate('editInformation'),
                                                    onPress: () => handleEditClick(row)
                                                },
                                                {
                                                    content: translate('delete'),
                                                    variant: 'danger',
                                                    onPress: () => showDeleteConfirmationDialog(row),
                                                }
                                            ]}
                                        />
                                    )
                                }
                            ]}
                            data={data?.rows ?? []}
                            progressPending={isLoading}
                        />
                    }
                    pagination={
                        <Pagination
                            totalItems={data?.count ?? 0}
                            currentPage={filters.page}
                            pageSize={filters.limit}
                            setPageSize={pageSize => {
                                setFilters(old => ({
                                    ...old,
                                    limit: pageSize,
                                    page: 0,
                                }));

                                navigation.setParams({
                                    ...route.params,
                                    limit: pageSize,
                                    page: 1,
                                });
                            }}
                            setPage={page => {
                                setFilters(old => ({
                                    ...old,
                                    page
                                }));

                                navigation.setParams({
                                    ...route.params,
                                    page: page + 1
                                });
                            }}
                        />
                    }
                />
            </Card>

            <ProvidenceModal
                providence={selectedProvidence}
                onClose={handleFormClose}
                isModalVisible={isModalVisible}
                authenticatedUser={authenticatedUser}
            />

            {selectedProvidence && (
                <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={isDeleteModalVisible}
                    itemName={selectedProvidence.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedProvidence);
                    }}
                />
            )}
        </Container>
    );
}
